import { useState } from 'react'
import type { ExerciseFill } from '../types'
import { useTranslation } from 'react-i18next'
import { useExerciseSlot } from '../hooks/useExerciseSet'

interface ExerciseFillBlankProps {
  exercise: ExerciseFill
  /** Position dans la série d'exercices du message — voir `ExerciseSetProvider`. */
  slotIndex: number
}

const NO_ANSWER = '—'

/** Split template on {_} tokens and return alternating text/blank segments */
function parseTemplate(template: string): Array<{ kind: 'text'; value: string } | { kind: 'blank'; index: number }> {
  const parts = template.split('{_}')
  const result: Array<{ kind: 'text'; value: string } | { kind: 'blank'; index: number }> = []
  parts.forEach((text, i) => {
    result.push({ kind: 'text', value: text })
    if (i < parts.length - 1) {
      result.push({ kind: 'blank', index: i })
    }
  })
  return result
}

export function ExerciseFillBlank({ exercise, slotIndex }: Readonly<ExerciseFillBlankProps>) {
  const { t } = useTranslation()
  const blanksCount = (exercise.template.match(/\{_\}/g) ?? []).length
  const [values, setValues] = useState<string[]>(new Array(blanksCount).fill(''))

  const segments = parseTemplate(exercise.template)

  const isBlankCorrect = (idx: number) => {
    const expected = exercise.answers[idx] ?? ''
    const given = (values[idx] ?? '').trim()
    return given.toLowerCase() === expected.toLowerCase()
  }

  // Un template sans aucun {_} est un JSON valide au sens du schéma Zod mais
  // sémantiquement incohérent (rien à remplir) : `values` vaut alors `[]`, et
  // `.every()` sur un tableau vide renvoie vrai par vacuité — sans le garde-fou
  // `hasBlanks &&`, l'exercice se comptait "répondu" et "juste" sans aucune
  // saisie, gonflant silencieusement le score envoyé au serveur. Le hook doit
  // rester appelé inconditionnellement (règle `react-hooks/rules-of-hooks`) :
  // le rendu d'erreur ci-dessous vient après, pas un `return` avant le hook.
  const hasBlanks = blanksCount > 0
  const allFilled = hasBlanks && values.every((v) => v.trim().length > 0)

  const { locked: submitted } = useExerciseSlot(slotIndex, allFilled, () => ({
    itemType: 'fill',
    question: exercise.question,
    answerLabel: hasBlanks ? values.map((v) => v.trim() || NO_ANSWER).join(' | ') : NO_ANSWER,
    correct: hasBlanks ? values.every((_, idx) => isBlankCorrect(idx)) : null,
    answered: allFilled,
  }))

  if (!hasBlanks) {
    return (
      <div className="my-3 rounded-xl border border-[var(--color-error)] bg-[var(--color-error-light)] px-4 py-3 text-sm text-[var(--color-error)]">
        {t('session.exercise_fill_no_blanks')}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-[var(--color-neutral-900)]">{exercise.question}</p>

      <p className="text-sm leading-8 text-[var(--color-neutral-800)]">
        {segments.map((seg, i) => {
          if (seg.kind === 'text') {
            return <span key={`text-${i}`}>{seg.value}</span>
          }
          const idx = seg.index
          // min-h-[44px] : la cible tactile mobile — .exercise-fill-input (index.css,
          // hors périmètre de ce lot) n'en fixe pas.
          let inputClass = 'exercise-fill-input min-h-[44px]'
          if (submitted) {
            inputClass += isBlankCorrect(idx) ? ' correct' : ' incorrect'
          }
          return (
            <input
              key={`blank-${idx}`}
              type="text"
              value={values[idx] ?? ''}
              disabled={submitted}
              onChange={(e) => {
                const next = [...values]
                next[idx] = e.target.value
                setValues(next)
              }}
              className={inputClass}
              aria-label={t('session.exercise_fill_blank_label', { n: idx + 1 })}
              style={{ minWidth: '80px', width: `${Math.max(80, ((values[idx] ?? '').length + 4) * 8)}px` }}
            />
          )
        })}
      </p>

      {submitted && (
        <div className="space-y-1">
          {values.map((_, idx) => (
            !isBlankCorrect(idx) && (
              <p key={`err-${idx}`} className="text-xs text-[var(--color-error)]">
                {t('session.exercise_fill_expected', { n: idx + 1, expected: exercise.answers[idx] })}
              </p>
            )
          ))}
          {values.every((_, idx) => isBlankCorrect(idx)) && (
            <p className="text-xs font-medium text-[var(--color-success)]">{t('session.all_answers_correct')}</p>
          )}
        </div>
      )}

      {submitted && exercise.explanation && (
        <div className="rounded-lg px-3 py-2 text-xs bg-[var(--color-primary-light)] text-[var(--color-neutral-700)]">
          <span className="font-semibold">{t('session.explanation')} </span>
          {exercise.explanation}
        </div>
      )}
    </div>
  )
}
